import React, { Suspense, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import { useTheme } from 'styled-components';
import { FullStory } from '@fullstory/browser';
import { useBreakpoints } from 'hooks/useWindowSize';
import { Modal } from 'shared/components/Modal/Modal';
import { LoadingSpinner } from 'shared/components/LoadingSpinner';
import { Button } from '../../../../shared/components/Button/Button';
import {
  ModalHeader,
  ModalText,
  TpvBaseContainer,
  TpvModalContent,
  TpvModalFooter,
} from './TpvModal.styles';
import { useGenerateTpvUrl } from '../../../../api/agreement';
import { toast } from '../../../../shared/components/Toast/Toast';

const TpvGraphic = React.lazy(() => import('shared/components/icons/TpvGraphic'));

export type TpvModalProps = {
  agreementId: string;
  isOpen: boolean;
  onCloseModal: () => void;
  onStartCallClickComplete: () => void;
};

export function TpvModal({
  agreementId,
  isOpen,
  onCloseModal,
  onStartCallClickComplete,
}: TpvModalProps) {
  const { t } = useTranslation();
  const screenSize = useBreakpoints();

  const theme = useTheme();
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const bodyList: string[] = t('tpvModal.body', {
    returnObjects: true,
    tpvAgentHoursWeekdays: t('tpvAgentHoursWeekdays'),
    tpvAgentHoursSaturday: t('tpvAgentHoursSaturday'),
    tpvAgentHoursSunday: t('tpvAgentHoursSunday'),
  }) as string[];

  const { mutateAsync: generateTpvUrlAsync } = useGenerateTpvUrl({
    onError(error: AxiosError) {
      setErrorMessage(error.message);
    },
  });

  useEffect(() => {
    if (errorMessage) {
      onCloseModal();
      toast({
        type: 'error',
        title: t('toast.error'),
        message: errorMessage,
        theme,
      });
    }
  }, [errorMessage, onCloseModal, t, theme]);

  const handleStartCallClick = async () => {
    try {
      setLoading(true);
      const tpvUrlResponse = await generateTpvUrlAsync({ agreement_id: agreementId });
      FullStory('trackEvent', {
        name: 'Start TPV Call - Clicked',
        properties: {
          status: 'success',
        },
      });
      // This is a known issue in Safari as it will not open new tab inside an async function.
      // Using then() does not work for latest Safari version.
      // Using setTimeout() works fine. setTimeout code runs on the main thread, instead of the asynchronous one
      // See: https://stackoverflow.com/questions/20696041/window-openurl-blank-not-working-on-imac-safari
      setTimeout(() => {
        window.open(tpvUrlResponse.url, '_blank');
      });
    } catch (error: any) {
      if (error instanceof AxiosError && (error as AxiosError).message) {
        setErrorMessage((error as AxiosError).message);
      } else {
        setErrorMessage(t('tpvModal.errorMessage'));
      }
      FullStory('trackEvent', {
        name: 'Start TPV Call - Clicked',
        properties: {
          status: 'error',
        },
      });
    } finally {
      onStartCallClickComplete();
    }
  };

  return (
    <Modal
      contentLabel={t('tpvModal.title')}
      styleVariant="tertiary"
      isOpen={isOpen}
      subHeader
      isFullWidth={screenSize === 'sm'}
      isFullHeight={screenSize === 'sm'}
      fitContent
      padding="24px"
      onRequestClose={onCloseModal}
    >
      <TpvModalContent>
        <TpvBaseContainer>
          <Suspense fallback={<LoadingSpinner />}>
            <TpvGraphic />
          </Suspense>
          <ModalHeader as="h2">{t('tpvModal.title')}</ModalHeader>
          {Array.isArray(bodyList) &&
            bodyList.map((item) => (
              <ModalText as="p">
                <Trans defaults={item} />
              </ModalText>
            ))}
          <TpvModalFooter>
            <Button
              label={t('tpvModal.cancelButton')}
              styleVariant="secondary"
              onClick={onCloseModal}
            />
            <Button
              label={t('tpvModal.startCallButton')}
              onClick={handleStartCallClick}
              isLoading={loading}
            />
          </TpvModalFooter>
        </TpvBaseContainer>
      </TpvModalContent>
    </Modal>
  );
}

export default TpvModal;
